<template>
  <!-- AI tokens used by a client and all its partitions (US 9391) -->
  <div class="cp-stack" qid="client-ai-usage">
    <div class="cp-usage-bar">
      <div>
        <h2 class="cp-page-title">{{ $t('client_page.ai_usage.title') }}</h2>
        <p class="cp-page-text">{{ $tc('client_page.ai_usage.intro', partitionCount, { name: client.name, count: partitionCount }) }}</p>
      </div>
      <span class="us-grow"></span>
      <div class="us-seg" role="tablist">
        <button v-for="option in periods"
                :key="option"
                type="button"
                role="tab"
                :class="{ 'is-on': period === option }"
                :aria-selected="period === option ? 'true' : 'false'"
                :qid="'ai-usage-period-' + option"
                @click="setPeriod(option)">
          {{ $t('client_page.ai_usage.period.' + option) }}
        </button>
      </div>
      <button type="button"
              class="pf-btn pf-btn--secondary"
              :disabled="!rows.length"
              qid="ai-usage-export"
              @click="exportCsv">
        <font-awesome-icon icon="file-download"/>{{ $t('client_page.ai_usage.export') }}
      </button>
    </div>

    <div v-if="period === 'custom'" class="cp-custom-period">
      <label>{{ $t('client_page.ai_usage.from') }}
        <b-form-input v-model="custom.from" type="date" qid="ai-usage-from" @change="load"/>
      </label>
      <label>{{ $t('client_page.ai_usage.to') }}
        <b-form-input v-model="custom.to" type="date" qid="ai-usage-to" @change="load"/>
      </label>
    </div>

    <div class="cp-kpis">
      <div class="cp-kpi" qid="ai-usage-kpi-month">
        <div class="cp-kpi__label">{{ $t('client_page.ai_usage.kpi.this_month', { month: monthName(thisMonth) }) }}</div>
        <div class="cp-kpi__value">{{ formatTokens(monthTotal(thisMonth)) }} <small>{{ $t('client_page.ai_usage.tokens') }}</small></div>
        <div class="cp-kpi__text">
          <span v-if="monthChange !== null" :class="monthChange > 0 ? 'cp-up' : 'cp-down'">{{ monthChange > 0 ? '▲' : '▼' }} {{ Math.abs(monthChange) }}%</span>
          {{ $t('client_page.ai_usage.kpi.vs_last_month', { month: monthName(lastMonth), value: formatTokens(monthTotal(lastMonth)) }) }}
        </div>
      </div>
      <div class="cp-kpi" qid="ai-usage-kpi-year">
        <div class="cp-kpi__label">{{ $t('client_page.ai_usage.kpi.twelve_months') }}</div>
        <div class="cp-kpi__value">{{ formatTokens(yearTotal) }} <small>{{ $t('client_page.ai_usage.tokens') }}</small></div>
        <div class="cp-kpi__text">{{ $tc('client_page.ai_usage.kpi.requests', yearRequests, { count: yearRequests.toLocaleString() }) }}</div>
      </div>
      <div class="cp-kpi" qid="ai-usage-kpi-biggest">
        <div class="cp-kpi__label">{{ $t('client_page.ai_usage.kpi.biggest') }}</div>
        <div class="cp-kpi__value cp-kpi__value--name">{{ biggest ? biggest.name : '–' }}</div>
        <div class="cp-kpi__text" v-if="biggest">{{ $t('client_page.ai_usage.kpi.share_of_period', { share: share(biggest) }) }}</div>
      </div>
      <div class="cp-kpi" qid="ai-usage-kpi-feature">
        <div class="cp-kpi__label">{{ $t('client_page.ai_usage.kpi.by_feature') }}</div>
        <div class="cp-kpi__value">{{ assistantShare }}% <small>{{ $t('client_page.ai_usage.feature.assistant') }}</small></div>
        <div class="cp-meter"><i :style="{ width: (grown ? assistantShare : 0) + '%' }"></i></div>
        <div class="cp-kpi__text">{{ 100 - assistantShare }}% {{ $t('client_page.ai_usage.feature.audit_plan_import') }}</div>
      </div>
    </div>

    <div class="v2-card">
      <div class="v2-card__head">
        <h3>{{ $t('client_page.ai_usage.chart_title') }}</h3>
        <div class="v2-legend cp-legend">
          <span v-for="series in chartSeries" :key="series.id"><i :style="{ background: series.color }"></i>{{ series.name }}</span>
        </div>
      </div>
      <div class="v2-card__body">
        <div class="cp-chart">
          <div v-for="(month, index) in chartMonths"
               :key="month.key"
               class="cp-chart__col"
               :title="monthName(month.key) + ': ' + formatTokens(month.total)">
            <div class="cp-chart__bar" :style="{ height: barHeight(month.total), transitionDelay: (index * 45) + 'ms' }">
              <i v-for="part in month.parts"
                 :key="part.id"
                 :style="{ height: (month.total ? part.total / month.total * 100 : 0) + '%', background: part.color }"></i>
            </div>
            <span class="cp-chart__label" :class="{ 'is-current': month.key === thisMonth }">{{ monthShort(month.key) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="v2-card">
      <table class="us-table cp-usage-table">
        <thead>
          <tr>
            <th>{{ $t('client_page.ai_usage.table.workspace') }}</th>
            <th class="cp-num">{{ $t('client_page.ai_usage.table.requests') }}</th>
            <th class="cp-num">{{ $t('client_page.ai_usage.table.input') }}</th>
            <th class="cp-num">{{ $t('client_page.ai_usage.table.output') }}</th>
            <th class="cp-num">{{ $t('client_page.ai_usage.table.total') }}</th>
            <th>{{ $t('client_page.ai_usage.table.share') }}</th>
            <th class="cp-chev-col"></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in rows">
            <tr :key="'row-' + row.id"
                class="cp-usage-row"
                :class="{ 'is-open': openRow === row.id }"
                qid="ai-usage-row"
                @click="toggle(row)">
              <td>
                <div class="cp-who">
                  <span class="pf-ws__logo">{{ initials(row.name) }}</span>
                  <span class="cp-who__name">{{ row.name }}</span>
                  <span v-if="row.is_client" class="v2-tag v2-tag--grey">{{ $t('client_page.ai_usage.client_tag') }}</span>
                </div>
              </td>
              <td class="cp-num">{{ row.requests.toLocaleString() }}</td>
              <td class="cp-num">{{ formatTokens(row.prompt_tokens) }}</td>
              <td class="cp-num">{{ formatTokens(row.completion_tokens) }}</td>
              <td class="cp-num"><b>{{ formatTokens(row.total_tokens) }}</b></td>
              <td>
                <div class="cp-share">
                  <div class="cp-share__track"><div class="cp-share__fill" :style="{ width: (grown ? share(row) : 0) + '%', background: colorOf(row.id) }"></div></div>
                  {{ share(row) }}%
                </div>
              </td>
              <td class="cp-chev-col"><font-awesome-icon :icon="openRow === row.id ? 'chevron-down' : 'chevron-right'"/></td>
            </tr>
            <tr v-if="openRow === row.id" :key="'detail-' + row.id" class="cp-usage-detail">
              <td colspan="7">
                <div class="cp-detail">
                  <div>
                    <div class="cp-label">{{ $t('client_page.ai_usage.by_user') }}</div>
                    <div v-for="user in row.users" :key="'u' + user.id" class="cp-detail__row">
                      <span>{{ user.name || $t('client_page.ai_usage.unknown_user') }}</span>
                      <span class="cp-num">{{ $tc('client_page.ai_usage.kpi.requests', user.requests, { count: user.requests }) }} · <b>{{ formatTokens(user.total_tokens) }}</b></span>
                    </div>
                    <div v-if="!row.users.length" class="pf-hint">{{ $t('client_page.ai_usage.no_usage') }}</div>
                  </div>
                  <div>
                    <div class="cp-label">{{ $t('client_page.ai_usage.by_feature') }}</div>
                    <div v-for="(sums, feature) in row.features" :key="feature" class="cp-detail__row">
                      <span>{{ $t('client_page.ai_usage.feature.' + feature) }}</span>
                      <span class="cp-num">{{ $tc('client_page.ai_usage.kpi.requests', sums.requests, { count: sums.requests }) }} · <b>{{ formatTokens(sums.total_tokens) }}</b></span>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
          <tr class="cp-usage-total">
            <td>{{ $tc('client_page.ai_usage.table.total_row', rows.length, { count: rows.length }) }}</td>
            <td class="cp-num">{{ totals.requests.toLocaleString() }}</td>
            <td class="cp-num">{{ formatTokens(totals.prompt_tokens) }}</td>
            <td class="cp-num">{{ formatTokens(totals.completion_tokens) }}</td>
            <td class="cp-num">{{ formatTokens(totals.total_tokens) }}</td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import moment from 'moment';

  // The client and the three busiest partitions get a colour in the chart, the rest are grey
  const COLORS = ['#2B8A80', '#3B6FD8', '#7B5CD6'];
  const CLIENT_COLOR = '#C77C1A';
  const OTHER_COLOR = '#C9D1DB';

  export default {
    name: 'ClientAiUsage',
    props: {
      client: Object
    },
    data() {
      return {
        periods: ['this_month', 'twelve_months', 'custom'],
        period: 'this_month',
        custom: { from: moment().startOf('month').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') },
        data: { workspaces: [], months: [] },
        openRow: null,
        // Bars start empty and grow once the numbers are in
        grown: false
      }
    },
    computed: {
      rows() {
        return this.data.workspaces || [];
      },
      partitionCount() {
        return this.rows.filter(row => !row.is_client).length;
      },
      totals() {
        let sum = key => this.rows.reduce((total, row) => total + row[key], 0);
        return { requests: sum('requests'), prompt_tokens: sum('prompt_tokens'), completion_tokens: sum('completion_tokens'), total_tokens: sum('total_tokens') };
      },
      thisMonth() {
        return moment().format('YYYY-MM');
      },
      lastMonth() {
        return moment().subtract(1, 'month').format('YYYY-MM');
      },
      monthChange() {
        let last = this.monthTotal(this.lastMonth);
        return last ? Math.round((this.monthTotal(this.thisMonth) - last) / last * 100) : null;
      },
      yearTotal() {
        return this.data.months.reduce((total, month) => total + month.total_tokens, 0);
      },
      yearRequests() {
        return this.data.months.reduce((total, month) => total + month.requests, 0);
      },
      biggest() {
        return this.rows.length && this.rows[0].total_tokens ? this.rows[0] : null;
      },
      assistantShare() {
        let assistant = this.rows.reduce((total, row) => total + ((row.features.assistant || {}).total_tokens || 0), 0);
        return this.totals.total_tokens ? Math.round(assistant / this.totals.total_tokens * 100) : 0;
      },
      // Workspaces coloured in the chart: the three biggest partitions over 12 months, then the client
      coloured() {
        let byWorkspace = {};
        this.data.months.forEach(month => {
          byWorkspace[month.client_id] = (byWorkspace[month.client_id] || 0) + month.total_tokens;
        });

        let partitions = this.rows.filter(row => !row.is_client)
          .sort((a, b) => (byWorkspace[b.id] || 0) - (byWorkspace[a.id] || 0))
          .slice(0, COLORS.length)
          .filter(row => byWorkspace[row.id]);
        let result = partitions.map((row, index) => ({ id: row.id, name: row.name, color: COLORS[index] }));
        let client = this.rows.find(row => row.is_client);
        if (client) {
          result.push({ id: client.id, name: this.$t('client_page.ai_usage.client_series', { name: client.name }), color: CLIENT_COLOR });
        }
        return result;
      },
      chartSeries() {
        let series = this.coloured.slice();
        if (this.rows.length > series.length) {
          series.push({ id: 'other', name: this.$t('client_page.ai_usage.other_partitions'), color: OTHER_COLOR });
        }
        return series;
      },
      chartMonths() {
        let months = [];
        for (let i = 11; i >= 0; i--) {
          let key = moment().subtract(i, 'month').format('YYYY-MM');
          let entries = this.data.months.filter(month => month.month === key);
          let parts = this.coloured.map(series => ({
            id: series.id,
            color: series.color,
            total: entries.filter(entry => entry.client_id === series.id).reduce((total, entry) => total + entry.total_tokens, 0)
          }));
          let colouredIds = this.coloured.map(series => series.id);
          parts.push({ id: 'other', color: OTHER_COLOR, total: entries.filter(entry => colouredIds.indexOf(entry.client_id) === -1).reduce((total, entry) => total + entry.total_tokens, 0) });
          months.push({ key: key, parts: parts.filter(part => part.total), total: entries.reduce((total, entry) => total + entry.total_tokens, 0) });
        }
        return months;
      },
      chartMax() {
        return Math.max(1, ...this.chartMonths.map(month => month.total));
      }
    },
    methods: {
      load() {
        let params = {};
        if (this.period === 'twelve_months') {
          params.from = moment().startOf('month').subtract(11, 'month').format('YYYY-MM-DD');
        } else if (this.period === 'custom') {
          params.from = this.custom.from;
          params.to = this.custom.to;
        }

        this.grown = false;
        piincHttp.get('clients/' + this.client.slug + '/ai-usage', { params: params }).then(response => {
          this.data = response.data;
          // Let the empty bars render first, so the change to their real size animates
          this.$nextTick(() => requestAnimationFrame(() => requestAnimationFrame(() => {
            this.grown = true;
          })));
        }, function() {});
      },
      setPeriod(period) {
        this.period = period;
        this.load();
      },
      toggle(row) {
        this.openRow = this.openRow === row.id ? null : row.id;
      },
      monthTotal(key) {
        return this.data.months.filter(month => month.month === key).reduce((total, month) => total + month.total_tokens, 0);
      },
      monthName(key) {
        return moment(key, 'YYYY-MM').format('MMMM');
      },
      monthShort(key) {
        return moment(key, 'YYYY-MM').format('MMM');
      },
      barHeight(total) {
        return this.grown ? (total / this.chartMax * 100) + '%' : '0%';
      },
      share(row) {
        return this.totals.total_tokens ? Math.round(row.total_tokens / this.totals.total_tokens * 100) : 0;
      },
      colorOf(id) {
        let series = this.coloured.find(item => item.id === id);
        return series ? series.color : OTHER_COLOR;
      },
      // 3,420,000 -> 3.42M, 52,000 -> 52K
      formatTokens(value) {
        if (value >= 1000000) {
          return (value / 1000000).toFixed(2).replace(/\.?0+$/, '') + 'M';
        }
        if (value >= 1000) {
          return Math.round(value / 1000) + 'K';
        }
        return String(value || 0);
      },
      initials(name) {
        let words = (name || '').trim().split(/\s+/).filter(Boolean);
        return words.slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase();
      },
      exportCsv() {
        let header = ['workspace', 'requests', 'input', 'output', 'total', 'share'].map(key => this.$t('client_page.ai_usage.table.' + key));
        let lines = [header].concat(this.rows.map(row => [row.name, row.requests, row.prompt_tokens, row.completion_tokens, row.total_tokens, this.share(row) + '%']));
        let csv = lines.map(line => line.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(';')).join('\r\n');

        let link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
        link.download = 'ai-usage-' + this.client.slug + '-' + this.data.from + '-' + this.data.to + '.csv';
        link.click();
        URL.revokeObjectURL(link.href);
      }
    },
    created() {
      this.load();
    }
  }
</script>
